// ---- Openings kit ----
// Shared helpers for windows and doorways, so each building doesn't re-implement
// them. Both work in the host structure's LOCAL coords via the build ctx.
//
//   - windowPane: a framed glass pane mounted proud of a wall face. Wrapped in
//     shell(nx,nz) with the wall's outward normal so it fades with that wall.
//   - doorway: splits a wall run around an opening (two flanks + a lintel wall
//     above), and adds a kept timber frame around the gap. When `c.door` is
//     present (game/server builds), the opening is an interactive door: closed
//     collider + client-side swinging leaf. Static `doorLeaf` mesh is only for
//     preview paths without the hook (so consolidate never bakes a non-swinging
//     leaf into the shell).
//
// The castle keeps its own bespoke leaded windows; new buildings should reach for
// these. `axis` is the run direction of the wall FACE (x for N/S walls whose
// normal is along z, z for E/W walls whose normal is along x).

import type { StructureBuildCtx } from "./define.ts";
import { shade } from "../color.ts";
import { doorLeaf } from "./trim.ts";

const DEF_FRAME = 0x5a3d28;  // dark oak trim
const DEF_GLASS = 0x95bcce;  // pale leaded glass

/** A framed glass window proud of a wall face, centred at height `cy`. `axis` is
 *  the wall face's run direction; (nx,nz) is the wall's outward normal.
 *  `style: "heavy"` (concept B) = thicker sill/jambs + deeper projection. */
export function windowPane(
  c: StructureBuildCtx,
  axis: "x" | "z", along: number, faceCoord: number,
  nx: 0 | 1 | -1, nz: 0 | 1 | -1, cy: number,
  opts: { w?: number; h?: number; frame?: number; glass?: number; style?: "slim" | "heavy" } = {},
) {
  const { box, shell, noShadow } = c;
  const w = opts.w ?? 1.5, h = opts.h ?? 1.7;
  const frame = opts.frame ?? DEF_FRAME, glass = opts.glass ?? DEF_GLASS;
  const mullion = shade(frame, 0.12);
  const heavy = opts.style === "heavy";
  const faceDir = (nx || nz) as 1 | -1;
  const fr = faceCoord + faceDir * (heavy ? 0.22 : 0.16);
  const glassC = faceCoord + faceDir * (heavy ? 0.08 : 0.06);
  const t = heavy ? 0.22 : 0.16;
  const depth = heavy ? 0.34 : 0.26;
  const sillD = heavy ? 0.42 : 0.34;
  const place = (a: number, y: number, fc: number, wx: number, hy: number, dz: number, col: number) =>
    axis === "x" ? box(a, y, fc, wx, hy, dz, col) : box(fc, y, a, dz, hy, wx, col);
  shell(nx, nz, () => {
    // Glass doesn't cast shadow — interior lamplight leaks out through the
    // pane (the frame + wall still occlude, so walls stay light-tight).
    noShadow(() => place(along, cy, glassC, w, h, 0.05, glass));
    place(along, cy + h / 2 + t / 2, fr, w + 2 * t, t, depth, frame);       // head
    place(along, cy - h / 2 - t / 2, fr, w + 2 * t, t * 1.25, sillD, frame); // sill
    place(along - w / 2 - t / 2, cy, fr, t, h, depth, frame);                // left jamb
    place(along + w / 2 + t / 2, cy, fr, t, h, depth, frame);                // right jamb
    place(along, cy, fr - faceDir * 0.04, heavy ? 0.12 : 0.1, h, depth * 0.75, mullion);
    place(along, cy, fr - faceDir * 0.04, w, heavy ? 0.12 : 0.1, depth * 0.75, mullion);
  });
}

export type DoorwayOpts = {
  /** "slim" = classic thin jambs (default, BC). "heavy" = concept-B chunky posts. */
  style?: "slim" | "heavy";
  /** Emit a plank door leaf inside the opening (visual only). Default false. */
  leaf?: boolean;
  /** Door-leaf wood color (defaults to a lighter shade of frame). */
  leafColor?: number;
  /** Closed-door collider / leaf foot Y. Defaults to `baseY`. When shell walls
   *  sink into a foundation pad (`baseY` below pad top), pass the pad/sill Y
   *  here so the box doesn't start below the walk surface. */
  colliderMinY?: number;
};

/** Carve a doorway into a solid wall run. `wallRect` is the wall's footprint
 *  (thin in its cross axis); `axis` is the run direction. The wall is emitted as
 *  two flanks either side of the opening + a lintel wall above it (collides above
 *  head height), plus a kept timber frame. With `c.door`, a closed-door collider
 *  is registered (interactive open/close); otherwise the gap stays walk-through. */
export function doorway(
  c: StructureBuildCtx,
  axis: "x" | "z",
  wallRect: { minX: number; maxX: number; minZ: number; maxZ: number },
  openCenter: number, openHalf: number, openH: number,
  baseY: number, wallTop: number,
  wallColor: number, frameColor = DEF_FRAME,
  opts: DoorwayOpts = {},
) {
  const { wall, box, keep } = c;
  const { minX, maxX, minZ, maxZ } = wallRect;
  const run0 = axis === "x" ? minX : minZ;
  const run1 = axis === "x" ? maxX : maxZ;
  const a0 = openCenter - openHalf, a1 = openCenter + openHalf;
  const heavy = opts.style === "heavy";

  // wall() takes a HEIGHT, and wallTop is an absolute Y — subtract baseY so an
  // elevated wall (upper-storey room) gets the right spans. baseY=0 callers are
  // unchanged.
  const fullH = wallTop - baseY;
  const lintelH = wallTop - baseY - openH;

  // two flanks (full height) either side of the opening
  if (axis === "x") {
    wall(run0, a0, minZ, maxZ, baseY, fullH, wallColor);
    wall(a1, run1, minZ, maxZ, baseY, fullH, wallColor);
    wall(a0, a1, minZ, maxZ, baseY + openH, lintelH, wallColor); // lintel wall
  } else {
    wall(minX, maxX, run0, a0, baseY, fullH, wallColor);
    wall(minX, maxX, a1, run1, baseY, fullH, wallColor);
    wall(minX, maxX, a0, a1, baseY + openH, lintelH, wallColor);  // lintel wall
  }

  // kept timber frame around the opening, centred in the wall thickness
  const cMid = axis === "x" ? (minZ + maxZ) / 2 : (minX + maxX) / 2;
  const wallThick = axis === "x" ? maxZ - minZ : maxX - minX;
  const cThick = wallThick + (heavy ? 0.45 : 0.2);
  const jambW = heavy ? 0.42 : 0.3;
  const lintelHBeam = heavy ? 0.5 : 0.4;
  const place = (a: number, y: number, runW: number, hy: number) =>
    axis === "x"
      ? box(a, baseY + y, cMid, runW, hy, cThick, frameColor)
      : box(cMid, baseY + y, a, cThick, hy, runW, frameColor);
  keep(() => {
    // Straddle the opening edge (not flush against it): if the jamb's inner face
    // sat exactly at a0/a1 it'd be coplanar with the flank wall's end face and
    // z-fight. Centring on the edge buries the flank end inside the thicker jamb.
    place(a0, openH / 2, jambW, openH);                         // jamb
    place(a1, openH / 2, jambW, openH);                         // jamb
    place(openCenter, openH + lintelHBeam / 2, openHalf * 2 + jambW * 2, lintelHBeam);  // lintel beam
    if (heavy) {
      // Threshold sill under the door.
      place(openCenter, 0.1, openHalf * 2 + jambW, 0.2);
    }
  });

  // Outward normal: prefer the face with larger |coord| so the leaf faces the
  // exterior on centered houses (same rule the old static leaf used).
  let nx: 0 | 1 | -1 = 0, nz: 0 | 1 | -1 = 0;
  let outer: number;
  if (axis === "x") {
    if (Math.abs(maxZ) >= Math.abs(minZ)) { nz = maxZ >= minZ ? 1 : -1; outer = maxZ; }
    else { nz = minZ >= maxZ ? 1 : -1; outer = minZ; }
  } else {
    if (Math.abs(maxX) >= Math.abs(minX)) { nx = maxX >= minX ? 1 : -1; outer = maxX; }
    else { nx = minX >= maxX ? 1 : -1; outer = minX; }
  }
  const faceDir = (nx || nz) as 1 | -1;
  // Leaf sits slightly inside the outer face (matches former doorLeaf recess).
  const doorFace = outer - faceDir * 0.08;
  const leafColor = opts.leafColor ?? shade(frameColor, 0.12);
  const thick = 0.22; // closed collider thickness (player radius ~0.45)
  // Hinge on the −run edge (left when looking along +run from outside).
  const hingeA = a0 + 0.06;
  // Leaf mesh extends +local-x from hinge; closedRy maps that into the wall plane.
  // openDelta swings outward (see plan — axis-x: −nz·π/2, axis-z: closed −π/2 + nx·π/2).
  const closedRy = axis === "x" ? 0 : -Math.PI / 2;
  const openDelta = axis === "x" ? -nz * (Math.PI / 2) : nx * (Math.PI / 2);
  const w = openHalf * 2 - 0.12;
  const h = openH;
  let hx: number, hy: number, hz: number, cx: number, cy: number, cz: number;
  let bMinX: number, bMaxX: number, bMinZ: number, bMaxZ: number;
  hy = baseY;
  cy = baseY + h / 2;
  if (axis === "x") {
    hx = hingeA; hz = doorFace;
    cx = openCenter; cz = doorFace;
    bMinX = a0 + 0.04; bMaxX = a1 - 0.04;
    bMinZ = doorFace - thick / 2; bMaxZ = doorFace + thick / 2;
  } else {
    hx = doorFace; hz = hingeA;
    cx = doorFace; cz = openCenter;
    bMinX = doorFace - thick / 2; bMaxX = doorFace + thick / 2;
    bMinZ = a0 + 0.04; bMaxZ = a1 - 0.04;
  }

  if (c.door) {
    // Interactive door: closed collider + client swinging leaf (not consolidated).
    // Collider foot may sit above sunk shell baseY (pad sill) — see colliderMinY.
    const colY0 = opts.colliderMinY ?? baseY;
    const leafHy = colY0;
    const leafCy = colY0 + h / 2;
    c.door({
      hx, hy: leafHy, hz, cx, cy: leafCy, cz, w, h,
      closedRy, openDelta,
      minX: bMinX, maxX: bMaxX, minZ: bMinZ, maxZ: bMaxZ,
      minY: colY0, maxY: colY0 + h,
      color: leafColor,
    });
  } else if (opts.leaf) {
    // Preview / mock path without the door hook — static decorative leaf only.
    doorLeaf(c, {
      at: openCenter, half: openHalf, h: openH, baseY,
      faceCoord: outer, nx, nz, axis,
      color: leafColor,
    });
  }
}
